import axios, { AxiosInstance } from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';

//API URL
export const prod = 'https://tips.southerndesigninc.com/api';
export const test = 'https://testtips.southerndesigninc.com/api';
export const local = 'https://localhost:44382';

//get base url each time called, to allow API switching across all calls
export function getBaseURL() {
  return (
    sessionStorage.getItem('baseUrl') ??
    localStorage.getItem('baseUrl') ??
    (window.location.href.indexOf('testtips.') > -1 || window.location.href.indexOf('localhost') > -1 ? test : prod)
  );
}

export function clearBaseURL() {
  sessionStorage.removeItem('baseUrl');
  localStorage.removeItem('baseUrl');
}

export function storeBaseURL(location: 'prod' | 'test' | 'local' | 'live') {
  let newLocation;
  switch (location.toLowerCase()) {
    case 'test':
      newLocation = test;
      break;
    case 'local':
      newLocation = local;
      break;
    case 'prod':
    case 'live':
    default:
      newLocation = prod;
  }

  sessionStorage.setItem('baseUrl', newLocation);
  localStorage.setItem('baseUrl', newLocation);
}

export function getBaseUrlName() {
  switch (getBaseURL()) {
    case prod:
      return 'Live';
    case test:
      return 'Test';
    case local:
      return 'Local';
    default:
      return 'None';
  }
}

export const refresh = (instance: AxiosInstance, accessToken: string, refreshToken: string): any => {
  const url = '/refresh';

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: null,
    },
  };

  let body = 'Token=' + encodeURIComponent(accessToken) + '&RefreshToken=' + encodeURIComponent(refreshToken);

  return instance
    .post(url, body, configAuth)
    .then((response) => response.data)
    .catch((err) => err);
};

//called if 401
const refreshAuth = (instance) => (failedRequest) => {
  //if expired refresh - else normal not authed
  if (failedRequest?.response?.headers?.['token-expired'] === 'true') {
    let accessToken = sessionStorage.getItem('accessToken') ?? localStorage.getItem('accessToken');
    let refreshToken = sessionStorage.getItem('refreshToken') ?? localStorage.getItem('refreshToken');
    if (accessToken && refreshToken) {
      return refresh(instance, accessToken, refreshToken).then((response) => {
        sessionStorage.setItem('accessToken', response.token);
        sessionStorage.setItem('refreshToken', response.refreshToken);
        sessionStorage.setItem('accessTokenExpiration', response.tokenExpiration);
        sessionStorage.setItem('refreshTokenExpiration', response.refreshTokenExpiration);
        if (localStorage.getItem('accessToken')) localStorage.setItem('accessToken', response.token);
        if (localStorage.getItem('accessTokenExpiration')) localStorage.setItem('accessTokenExpiration', response.tokenExpiration);
        if (localStorage.getItem('refreshToken')) localStorage.setItem('refreshToken', response.refreshToken);
        if (localStorage.getItem('refreshTokenExpiration')) localStorage.setItem('refreshTokenExpiration', response.refreshTokenExpiration);

        return Promise.resolve();
      });
    }
  } else {
    return Promise.resolve(failedRequest);
  }
};

// Obtain the fresh token each time the function is called
export function getAccessToken() {
  return sessionStorage.getItem('accessToken');
}

// Obtain the fresh token each time the function is called
function getTokenExpiration() {
  var strExp = sessionStorage.getItem('accessTokenExpiration');
  return strExp ? new Date(Date.parse(strExp) - 5 * 60 * 1000) : new Date(0); //allow 5 mins desync time to server
}

function refreshTokens(instance) {
  var accessToken = sessionStorage.getItem('accessToken') ?? localStorage.getItem('accessToken') ?? '';
  var refreshToken = sessionStorage.getItem('refreshToken') ?? localStorage.getItem('refreshToken') ?? '';
  if (accessToken === '' || refreshToken === '') return Promise.reject(new axios.Cancel('Invalid Token(s)'));
  else
    return refresh(instance, accessToken, refreshToken).then((response) => {
      sessionStorage.setItem('accessToken', response.token);
      sessionStorage.setItem('refreshToken', response.refreshToken);
      sessionStorage.setItem('accessTokenExpiration', response.tokenExpiration);
      sessionStorage.setItem('refreshTokenExpiration', response.refreshTokenExpiration);
      if (localStorage.getItem('accessToken')) localStorage.setItem('accessToken', response.token);
      if (localStorage.getItem('accessTokenExpiration')) localStorage.setItem('accessTokenExpiration', response.tokenExpiration);
      if (localStorage.getItem('refreshToken')) localStorage.setItem('refreshToken', response.refreshToken);
      if (localStorage.getItem('refreshTokenExpiration')) localStorage.setItem('refreshTokenExpiration', response.refreshTokenExpiration);
    });
}

const AxiosConfig = () => {
  var instance: AxiosInstance;
  var refreshPromise: Promise<any> | undefined;
  const getInstance = () => {
    if (!instance) {
      instance = axios.create({ headers: { 'Access-Control-Allow-Origin': '*' } });
      createAuthRefreshInterceptor(instance, refreshAuth(instance));
      // Use interceptor to inject the token to requests
      instance.interceptors.request.use((request) => {
        request.baseURL = request.baseURL ?? getBaseURL();
        request.headers['Authorization'] = 'bearer ' + getAccessToken();
        if (request.url === '/refresh' || request.url === '/authentication' || request.url === '/accounts/sendpasswordreset') return request;

        if (!refreshPromise && new Date().getTime() > getTokenExpiration().getTime()) {
          refreshPromise = refreshTokens(getInstance())
            .then(() => {
              refreshPromise = undefined;
              return request;
            })
            .catch((err) => {
              refreshPromise = undefined;
              return Promise.reject(new axios.Cancel(err));
            });
          return refreshPromise ?? request;
        } else {
          return refreshPromise
            ? refreshPromise.then(() => {
                return request;
              })
            : request;
        }
      });
    }

    return instance;
  };
  return {
    instance: getInstance,
    refresh: () => refreshTokens(getInstance()),
  };
};

export default AxiosConfig();
